import type { CurrentUser, StaffUserPublic } from '@cfcg/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from './api.js';

export const CURRENT_USER_KEY = ['auth', 'me'] as const;

/**
 * Stan zalogowania. Brak sesji to normalny stan aplikacji, nie błąd —
 * dlatego 401 zamieniamy na `null` zamiast pozwalać mu wypłynąć jako wyjątek.
 */
export function useCurrentUser() {
  return useQuery<CurrentUser | null>({
    queryKey: CURRENT_USER_KEY,
    queryFn: async () => {
      try {
        return await api.get<CurrentUser>('/api/auth/me');
      } catch (error) {
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          return null;
        }
        throw error;
      }
    },
    retry: false,
    staleTime: 30_000,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api.post<CurrentUser>('/api/auth/login', input),
    onSuccess: (data) => queryClient.setQueryData(CURRENT_USER_KEY, data),
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: true }>('/api/auth/logout'),
    // Czyścimy CAŁY cache, nie tylko sesję — inaczej lista pracowników
    // poprzedniej osoby zostałaby na ekranie logowania następnej.
    onSettled: async () => {
      queryClient.setQueryData(CURRENT_USER_KEY, null);
      await queryClient.invalidateQueries();
    },
  });
}

export function useChangeOwnPassword() {
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      api.post<{ ok: true }>('/api/auth/change-password', input),
  });
}

// --- zarządzanie pracownikami (tylko administrator) ---

export const STAFF_KEY = ['admin', 'staff'] as const;

export function useStaffList(enabled: boolean) {
  return useQuery({
    queryKey: STAFF_KEY,
    queryFn: () => api.get<{ users: StaffUserPublic[] }>('/api/admin/staff'),
    enabled,
  });
}

export function useStaffMutations() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: STAFF_KEY });

  return {
    create: useMutation({
      mutationFn: (input: {
        email: string;
        displayName: string;
        role: 'admin' | 'support';
        password: string;
      }) => api.post<{ user: StaffUserPublic }>('/api/admin/staff', input),
      onSuccess: refresh,
    }),
    update: useMutation({
      mutationFn: (input: {
        id: string;
        changes: { role?: 'admin' | 'support'; isActive?: boolean; displayName?: string };
      }) => api.patch<{ user: StaffUserPublic }>(`/api/admin/staff/${input.id}`, input.changes),
      onSuccess: refresh,
    }),
    resetPassword: useMutation({
      mutationFn: (input: { id: string; password: string }) =>
        api.post<{ ok: true }>(`/api/admin/staff/${input.id}/password`, {
          password: input.password,
        }),
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.delete<{ ok: true }>(`/api/admin/staff/${id}`),
      onSuccess: refresh,
    }),
  };
}

export function messageOf(error: unknown): string {
  return error instanceof ApiError ? error.message : 'Wystąpił nieoczekiwany błąd.';
}
